import { FileText, Hash, LogOut, ShieldCheck } from 'lucide-react'
import { Logo } from './Logo'
import { ThemeToggle } from './ThemeToggle'
import { getInitials } from '@/lib/format'
import { useShellStore } from '@/stores/shellStore'
import { useUserStore } from '@/stores/userStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

async function logout() {
  try {
    await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' })
  } catch {
    // Ignore network errors — clear local state and redirect anyway.
  }
  useUserStore.setState({ User: null })
  window.location.href = '/authentication'
}

export function Topbar({ orgName = 'WHCA Notify' }: { orgName?: string }) {
  const user = useUserStore((s) => s.User)
  const openTemplates = useShellStore((s) => s.openTemplates)
  const openNumberPool = useShellStore((s) => s.openNumberPool)
  const openDepartments = useShellStore((s) => s.openDepartments)
  const initials = getInitials(user?.givenName || user?.displayName)

  return (
    <header className="z-10 flex h-[60px] flex-none items-center justify-between border-b border-border bg-surface px-[18px]">
      <div className="flex items-center gap-2.5">
        <Logo size="sm" />
        <span className="text-[17px] font-extrabold tracking-[-0.02em]">{orgName}</span>
      </div>
      <div className="flex items-center gap-2.5">
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-primary text-[13px] font-bold text-on-primary"
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.displayName}</p>
                <p className="text-xs leading-none text-muted-foreground">{user?.mail}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user?.isSuperAdmin && (
              <>
                <DropdownMenuItem onClick={openTemplates} className="cursor-pointer">
                  <FileText className="mr-2 h-4 w-4" />
                  Manage templates
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openNumberPool} className="cursor-pointer">
                  <Hash className="mr-2 h-4 w-4" />
                  Group chat numbers
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openDepartments} className="cursor-pointer">
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Departments
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
